import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ModalController } from '@ionic/angular';
import {
  TBctxHeaderItem,
  TBctxItemWithoutSize,
  TBulkGradingPageSizeItem,
  TBulkWashingEventType,
  TBulkGradingPageSizeItemVM,
  BCTX_KEYS_TO_DISPLAY_MAP,
  TVendorDTO,
  TCountEvent,
} from '@monorepo/types';
import { EventStack, InputAppAPIHelperUtil } from '@monorepo/utils';
import produce from 'immer';
import { SizeFilterComponent } from '../../md-point/size-filter/size-filter.component';

@Component({
  selector: 'monorepo-bulk-count-grading-page',
  templateUrl: './bulk-count-grading-page.component.html',
  styleUrls: ['./bulk-count-grading-page.component.scss'],
})
export class BulkCountGradingPageComponent implements OnInit {
  @Input() bctxKeysToDisplayMap = BCTX_KEYS_TO_DISPLAY_MAP;
  @Input() isVendorEnabled = false;
  @Input() type: TBulkWashingEventType = 'SEND';
  @Input() vendor!: TVendorDTO;
  sizeListVM: TBulkGradingPageSizeItemVM[] = [];
  @Input() bctxWithoutSizeList: TBctxItemWithoutSize[] = [];
  @Input() set sizeList(sizeList: TBulkGradingPageSizeItem[]) {
    let showMessageForCountsReduction = false;
    sizeList.forEach((sizeInfo) => {
      const index = this.sizeListVM.findIndex((sizeVM) => sizeVM.savedData.name === sizeInfo.name);
      if (index === -1) {
        if (sizeInfo.wipQty < sizeInfo.activeQty) showMessageForCountsReduction = true;
        this.sizeListVM = produce(this.sizeListVM, (draft) => {
          draft.push({
            savedData: sizeInfo,
            currentCount: sizeInfo.wipQty >= sizeInfo.activeQty ? sizeInfo.activeQty : sizeInfo.wipQty,
          });
        });
      } else {
        if (sizeInfo.wipQty < this.sizeListVM[index].currentCount) showMessageForCountsReduction = true;
        this.sizeListVM = produce(this.sizeListVM, (draft) => {
          draft[index].savedData = sizeInfo;
          draft[index].currentCount =
            sizeInfo.wipQty >= draft[index].currentCount ? draft[index].currentCount : sizeInfo.wipQty;
        });
      }
    });
    if (showMessageForCountsReduction) {
      this.inputAppAPIHelperUtil.showFalureToastWithDuration(
        'Not enough wip available for some sizes, reducing the saved work',
        10000,
        true,
      );
      this.updatePendingTransaction.emit(
        this.sizeListVM.reduce((acc, curr) => {
          acc[curr.savedData.name] = curr.currentCount;
          return acc;
        }, {} as Record<string, number>),
      );
    }
    this.#updateVisibleListAndTotalActuals();
  }
  @Input() enableBulkChange = true;
  @Input() proceedTxt = 'Send for washing';

  @Output() proceed = new EventEmitter<TBulkGradingPageSizeItem[]>();
  @Output() exit = new EventEmitter<string>();
  @Output() saveAndExit = new EventEmitter<TBulkGradingPageSizeItem[]>();
  @Output() sync = new EventEmitter<string>();
  @Output() updatePendingTransaction = new EventEmitter<Record<string, number>>();

  totalQty = 0;
  visibleSizeList: TSizeAndVisibleStatus[] = [];

  undoIcon = '/assets/md-point/undo-icon.svg';
  sizeFilterIcon = '/assets/md-point/size-filter.svg';
  syncIcon = '/assets/md-point/sync-icon.svg';
  proceedIcon = '/assets/fabric-inspection/proceed-icon.svg';
  goBackIcon = '/assets/left-arrow.svg';

  headerInput: TBctxHeaderItem[] = [];
  count = 1;
  eventStack = new EventStack<TCountEvent>();

  constructor(private modalController: ModalController, private inputAppAPIHelperUtil: InputAppAPIHelperUtil) {}

  ngOnInit(): void {
    this.#updateVisibleListAndTotalActuals();
    this.#createHeaderInput();
  }

  #createHeaderInput() {
    const headerInput: TBctxHeaderItem[] = this.bctxWithoutSizeList.map((item) => {
      return {
        label: this.bctxKeysToDisplayMap[item.k],
        value: item.v,
      };
    });
    if (this.isVendorEnabled && this.type === 'RECEIVE') {
      const headerInputItem: TBctxHeaderItem = {
        label: 'Rcvd from',
        value: this.vendor.vendorName,
      };
      this.headerInput = [headerInputItem, ...headerInput];
    } else this.headerInput = headerInput;
  }

  #updateVisibleListAndTotalActuals() {
    const visibleSizeList: TSizeAndVisibleStatus[] = [];
    this.sizeListVM.forEach((sizeItem) => {
      const visibleSizeListItem = this.visibleSizeList.find((item) => item.size === sizeItem.savedData.name);
      if (visibleSizeListItem) visibleSizeList.push(visibleSizeListItem);
      else visibleSizeList.push({ size: sizeItem.savedData.name, status: true });
    });
    this.visibleSizeList = visibleSizeList;
    this.updateTotalActuals();
  }

  #getIndexOfSizeInSizeList(size: string) {
    let foundIndex = -1;
    this.sizeListVM.forEach((item, index) => {
      if (item.savedData.name === size) {
        foundIndex = index;
      }
    });
    return foundIndex;
  }

  updateActiveQty(sizeName: string) {
    const itemIndex = this.#getIndexOfSizeInSizeList(sizeName);
    if (itemIndex > -1) {
      if (this.sizeListVM[itemIndex].currentCount + this.count > this.sizeListVM[itemIndex].savedData.wipQty) {
        this.inputAppAPIHelperUtil.showPopup('Validation Failed!', 'Count exceed the size wip quantity');
      } else {
        this.sizeListVM = produce(this.sizeListVM, (draft) => {
          draft[itemIndex].currentCount = draft[itemIndex].currentCount + this.count;
        });
        this.eventStack.push({
          bctxKey: '',
          sizeName,
          count: this.count,
        });
      }
    }
    this.updateTotalActuals();
  }

  updateTotalActuals() {
    let total = 0;
    this.sizeListVM.forEach((item) => {
      total += item.currentCount;
    });
    this.totalQty = total;
  }

  async filterSizeItem() {
    const popup = await this.modalController.create({
      component: SizeFilterComponent,
      componentProps: {
        sizeList: this.sizeListVM.map((sizeVM) => sizeVM.savedData),
        filteredList: this.visibleSizeList.map((item) => item.status),
      },
      backdropDismiss: true,
    });
    popup.present();
    popup.onDidDismiss().then((res) => {
      console.log('[DATA]', res);
      if (res.data) {
        this.visibleSizeList = this.visibleSizeList.map((item, index) => {
          return { ...item, status: res.data[index] };
        });
      }
    });
  }

  handleExit() {
    this.exit.emit('EXIT');
  }

  getOutputFormat() {
    return this.sizeListVM
      .map((sizeInfo) => {
        return produce(sizeInfo.savedData, (draft) => {
          draft.activeQty = sizeInfo.currentCount;
        });
      })
      .filter((data) => data.activeQty > 0);
  }

  handleSaveAndExit() {
    this.saveAndExit.emit(this.getOutputFormat());
  }

  handleProceed() {
    this.proceed.emit(this.getOutputFormat());
  }

  handleUndo() {
    if (this.eventStack.isEmpty()) {
      if (this.totalQty > 0) {
        this.sizeListVM = this.sizeListVM.map((item, index) => {
          return produce(this.sizeListVM[index], (draft) => {
            draft.currentCount = 0;
          });
        });
      } else this.inputAppAPIHelperUtil.showPopup('Warning', 'Nothing to undo');
    } else {
      const eventItem = this.eventStack.peek();
      this.eventStack.pop();
      const itemIndex = this.#getIndexOfSizeInSizeList(eventItem.sizeName);
      if (itemIndex > -1) {
        this.sizeListVM = produce(this.sizeListVM, (draft) => {
          draft[itemIndex].currentCount -= eventItem.count;
        });
      }
    }
    this.updateTotalActuals();
  }

  handleBulkCount(bulkCount: number) {
    this.count = bulkCount;
  }

  async handleSync() {
    this.eventStack = new EventStack<TCountEvent>();
    this.sync.emit('SYNC');
  }
}

type TSizeAndVisibleStatus = {
  size: string;
  status: boolean;
};
